/**
 * Mock ModernizeIT API Adapter
 * Simulates the ModernizeIT API for development/testing
 *
 * In production, this would be replaced with the real API adapter
 * that calls http://localhost:8000
 */

import { JobType, JobStatus } from '../../core/entities/Job'

// Helper to simulate async delay
const delay = (ms = 100) => new Promise(resolve => setTimeout(resolve, ms))

// Helper to generate job IDs
const generateJobId = (type, scoutAccountId, applicationName) => {
  const prefixes = {
    [JobType.INGEST]: 'ingest_job',
    [JobType.CODE_ANALYSIS]: 'ca',
    [JobType.CODE_REFACTOR]: 'rf',
    [JobType.DEPENDENCY_MAPPER]: 'dm',
    [JobType.MONOLITH_IDENTIFIER]: 'mi',
    [JobType.DATA_ANALYSIS]: 'da',
    [JobType.DISCOVERY]: 'discovery',
    [JobType.ARCHITECTURE]: 'arch',
  }
  const prefix = prefixes[type] || type
  const timestamp = Date.now().toString(36)
  return `${prefix}_${scoutAccountId}_${applicationName}_${timestamp}`
}

// Simulated job statuses (for polling simulation)
const runningJobs = new Map()

export class MockModernizeAPIAdapter {
  constructor(baseUrl = 'http://localhost:8000') {
    this.baseUrl = baseUrl
  }

  // ===== Health =====

  async healthCheck() {
    await delay(50)
    return { status: 'healthy', mock: true }
  }

  // ===== Ingest =====

  async startIngest({ scoutAccountId, applicationName, sourcePath, sourceLanguage = 'cobol', targetLanguage = 'java' }) {
    await delay(200)
    const jobId = generateJobId(JobType.INGEST, scoutAccountId, applicationName)

    // Simulate job progress
    runningJobs.set(jobId, {
      type: JobType.INGEST,
      status: JobStatus.RUNNING,
      progress: 0,
      startTime: Date.now(),
      duration: 3000, // 3 seconds to complete
    })

    return { jobId }
  }

  async getIngestStatus(jobId) {
    await delay(50)
    return this._getSimulatedJobStatus(jobId)
  }

  async getIngestResults(jobId) {
    await delay(100)
    return {
      filesProcessed: 3,
      totalLines: 850,
      files: [
        { name: 'LGACUS01.cbl', lines: 277, status: 'processed' },
        { name: 'LGACDB01.cbl', lines: 45, status: 'processed' },
        { name: 'LGICUS01.cbl', lines: 42, status: 'processed' },
      ],
    }
  }

  // ===== Code Analysis =====

  async startCodeAnalysis({ scoutAccountId, applicationName, generateJava = true, generateGraphs = true }) {
    await delay(200)
    const jobId = generateJobId(JobType.CODE_ANALYSIS, scoutAccountId, applicationName)

    runningJobs.set(jobId, {
      type: JobType.CODE_ANALYSIS,
      status: JobStatus.RUNNING,
      progress: 0,
      startTime: Date.now(),
      duration: 5000,
    })

    return { jobId }
  }

  async getCodeAnalysisStatus(jobId) {
    await delay(50)
    return this._getSimulatedJobStatus(jobId)
  }

  async getCodeAnalysisResults(jobId) {
    await delay(100)
    return {
      programsAnalyzed: 3,
      javaFilesGenerated: 3,
      artifacts: ['structure.json', 'metrics.json', 'callgraph.json'],
      graphs: ['callgraph.png', 'structure.png'],
    }
  }

  async getCodeAnalysisArtifact(jobId, filename) {
    await delay(100)
    // Return mock JSON based on filename
    if (filename === 'metrics.json') {
      return {
        totalPrograms: 3,
        totalLines: 850,
        complexity: { low: 2, medium: 1, high: 0 },
      }
    }
    return { mock: true, filename }
  }

  async getCodeAnalysisGraph(jobId, filename) {
    await delay(100)
    // Return mock blob
    return new Blob(['mock graph'], { type: 'image/png' })
  }

  // ===== Code Refactor =====

  async startCodeRefactor({ scoutAccountId, applicationName, mode = 'analyze', useAI = true }) {
    await delay(200)
    const jobId = generateJobId(JobType.CODE_REFACTOR, scoutAccountId, applicationName)

    runningJobs.set(jobId, {
      type: JobType.CODE_REFACTOR,
      status: JobStatus.RUNNING,
      progress: 0,
      startTime: Date.now(),
      duration: 4000,
    })

    return { jobId }
  }

  async transformCode({ scoutAccountId, applicationName, recipesToApply = 'all' }) {
    await delay(200)
    const jobId = generateJobId(JobType.CODE_REFACTOR, scoutAccountId, applicationName)

    runningJobs.set(jobId, {
      type: JobType.CODE_REFACTOR,
      status: JobStatus.RUNNING,
      progress: 0,
      startTime: Date.now(),
      duration: 6000,
    })

    return { jobId }
  }

  async getCodeRefactorStatus(jobId) {
    await delay(50)
    return this._getSimulatedJobStatus(jobId)
  }

  async getCodeRefactorResults(jobId) {
    await delay(100)
    return {
      filesTransformed: 3,
      recipesApplied: 12,
      improvements: ['Modernized date handling', 'Added null safety', 'Improved exception handling'],
    }
  }

  async getCodeRefactorRecipes(jobId) {
    await delay(100)
    return [
      { id: 'r1', name: 'ModernizeDateHandling', applicable: true, impact: 'medium' },
      { id: 'r2', name: 'AddNullSafety', applicable: true, impact: 'high' },
      { id: 'r3', name: 'ImproveExceptionHandling', applicable: true, impact: 'medium' },
    ]
  }

  // ===== Dependency Mapper =====

  async startDependencyMapper({ scoutAccountId, applicationName, sourceType }) {
    await delay(200)
    const jobId = `dm_${sourceType}_${scoutAccountId}_${applicationName}_${Date.now().toString(36)}`

    runningJobs.set(jobId, {
      type: JobType.DEPENDENCY_MAPPER,
      status: JobStatus.RUNNING,
      progress: 0,
      startTime: Date.now(),
      duration: 3000,
    })

    return { jobId }
  }

  async compareDependencies({ scoutAccountId, applicationName }) {
    await delay(200)
    const jobId = `dm_compare_${scoutAccountId}_${applicationName}_${Date.now().toString(36)}`

    runningJobs.set(jobId, {
      type: JobType.DEPENDENCY_MAPPER,
      status: JobStatus.RUNNING,
      progress: 0,
      startTime: Date.now(),
      duration: 4000,
    })

    return { jobId }
  }

  async getDependencyMapperStatus(jobId) {
    await delay(50)
    return this._getSimulatedJobStatus(jobId)
  }

  async getDependencyMapperResults(jobId) {
    await delay(100)
    return {
      totalDependencies: 15,
      externalCalls: 5,
      dbCalls: 8,
      dependencies: [
        { from: 'LGACUS01', to: 'LGACDB01', type: 'call' },
        { from: 'LGACUS01', to: 'CUSTOMER_TABLE', type: 'sql' },
      ],
    }
  }

  // ===== Monolith Identifier =====

  async startMonolithIdentifier({ scoutAccountId, applicationName, sourceType }) {
    await delay(200)
    const jobId = `mi_${sourceType}_${scoutAccountId}_${applicationName}_${Date.now().toString(36)}`

    runningJobs.set(jobId, {
      type: JobType.MONOLITH_IDENTIFIER,
      status: JobStatus.RUNNING,
      progress: 0,
      startTime: Date.now(),
      duration: 3000,
    })

    return { jobId }
  }

  async compareMonolithPatterns({ scoutAccountId, applicationName }) {
    await delay(200)
    const jobId = `mi_compare_${scoutAccountId}_${applicationName}_${Date.now().toString(36)}`

    runningJobs.set(jobId, {
      type: JobType.MONOLITH_IDENTIFIER,
      status: JobStatus.RUNNING,
      progress: 0,
      startTime: Date.now(),
      duration: 4000,
    })

    return { jobId }
  }

  async getMonolithIdentifierStatus(jobId) {
    await delay(50)
    return this._getSimulatedJobStatus(jobId)
  }

  async getMonolithIdentifierResults(jobId) {
    await delay(100)
    return {
      antiPatterns: 3,
      hotspots: 2,
      findings: [
        { type: 'god_class', severity: 'high', location: 'LGACUS01' },
        { type: 'tight_coupling', severity: 'medium', location: 'LGACDB01' },
      ],
    }
  }

  // ===== Data Analysis =====

  async startDataAnalysis({ scoutAccountId, applicationName }) {
    await delay(200)
    const jobId = generateJobId(JobType.DATA_ANALYSIS, scoutAccountId, applicationName)

    runningJobs.set(jobId, {
      type: JobType.DATA_ANALYSIS,
      status: JobStatus.RUNNING,
      progress: 0,
      startTime: Date.now(),
      duration: 4000,
    })

    return { jobId }
  }

  async getDataAnalysisStatus(jobId) {
    await delay(50)
    return this._getSimulatedJobStatus(jobId)
  }

  async getDataAnalysisResults(jobId) {
    await delay(100)
    return {
      tablesIdentified: 5,
      copybooksProcessed: 3,
      dataFlows: 12,
    }
  }

  async getERD(jobId) {
    await delay(100)
    return {
      tables: [
        { name: 'CUSTOMER_TABLE', columns: ['CUST_ID', 'FIRST_NAME', 'LAST_NAME', 'EMAIL'] },
        { name: 'ORDER_TABLE', columns: ['ORDER_ID', 'CUST_ID', 'ORDER_DATE', 'TOTAL'] },
      ],
      relationships: [
        { from: 'ORDER_TABLE', to: 'CUSTOMER_TABLE', type: 'many-to-one', key: 'CUST_ID' },
      ],
    }
  }

  async getDataLineage(jobId) {
    await delay(100)
    return {
      flows: [
        { source: 'CUSTOMER_INPUT', target: 'WS-CUSTOMER-RECORD', transform: 'MOVE' },
        { source: 'WS-CUSTOMER-RECORD', target: 'CUSTOMER_TABLE', transform: 'INSERT' },
      ],
    }
  }

  // ===== Discovery =====

  async startDiscovery({ scoutAccountId, applicationName, enableAI = true }) {
    await delay(200)
    const jobId = generateJobId(JobType.DISCOVERY, scoutAccountId, applicationName)

    runningJobs.set(jobId, {
      type: JobType.DISCOVERY,
      status: JobStatus.RUNNING,
      progress: 0,
      startTime: Date.now(),
      duration: 5000,
    })

    return { jobId }
  }

  async calculateROI(params) {
    await delay(300)
    return {
      estimatedSavings: 500000,
      paybackPeriod: '18 months',
      fiveYearROI: '320%',
      recommendation: 'Proceed with modernization',
    }
  }

  async getDiscoveryResults(jobId, section) {
    await delay(100)
    if (section === 'roi_analysis') {
      return { estimatedSavings: 500000, paybackPeriod: '18 months' }
    }
    if (section === 'business_processes') {
      return { processes: ['Customer Management', 'Order Processing', 'Reporting'] }
    }
    return {
      totalLOC: 850,
      complexity: 'medium',
      businessProcesses: 3,
      estimatedEffort: '6-9 months',
    }
  }

  async getROIAssumptions() {
    await delay(100)
    return {
      cloudCostPerMIPS: 500,
      javaDevSalary: 95000,
      productivityGain: 0.3,
    }
  }

  // ===== Architecture =====

  async startArchitecture({ scoutAccountId, applicationName, generateIaC = true }) {
    await delay(200)
    const jobId = generateJobId(JobType.ARCHITECTURE, scoutAccountId, applicationName)

    runningJobs.set(jobId, {
      type: JobType.ARCHITECTURE,
      status: JobStatus.RUNNING,
      progress: 0,
      startTime: Date.now(),
      duration: 6000,
    })

    return { jobId }
  }

  async getArchitectureStatus(jobId) {
    await delay(50)
    return this._getSimulatedJobStatus(jobId)
  }

  async getArchitectureResults(jobId, section) {
    await delay(100)
    if (section === 'cost_estimate') {
      return { monthly: 2500, yearly: 30000, details: [] }
    }
    return {
      recommendation: 'AWS ECS with RDS PostgreSQL',
      services: ['ECS', 'RDS', 'S3', 'CloudWatch'],
      estimatedMonthlyCost: 2500,
    }
  }

  async getIaCTemplates(jobId) {
    await delay(100)
    return ['main.tf', 'variables.tf', 'outputs.tf', 'ecs.tf', 'rds.tf']
  }

  async getIaCTemplateFile(jobId, filename) {
    await delay(100)
    return `# ${filename}\n# Generated IaC template\n# TODO: Fill in actual template`
  }

  // ===== Generic Job Methods =====

  async getJobStatus(jobType, jobId) {
    await delay(50)
    return this._getSimulatedJobStatus(jobId)
  }

  async getJobResults(jobType, jobId) {
    await delay(100)
    // Route to specific result method
    const methods = {
      [JobType.INGEST]: () => this.getIngestResults(jobId),
      [JobType.CODE_ANALYSIS]: () => this.getCodeAnalysisResults(jobId),
      [JobType.CODE_REFACTOR]: () => this.getCodeRefactorResults(jobId),
      [JobType.DEPENDENCY_MAPPER]: () => this.getDependencyMapperResults(jobId),
      [JobType.MONOLITH_IDENTIFIER]: () => this.getMonolithIdentifierResults(jobId),
      [JobType.DATA_ANALYSIS]: () => this.getDataAnalysisResults(jobId),
      [JobType.DISCOVERY]: () => this.getDiscoveryResults(jobId),
      [JobType.ARCHITECTURE]: () => this.getArchitectureResults(jobId),
    }
    return methods[jobType]?.() || { mock: true }
  }

  async getJobArtifact(jobType, jobId, filename) {
    await delay(100)
    return { mock: true, jobType, jobId, filename }
  }

  // ===== Private Helpers =====

  _getSimulatedJobStatus(jobId) {
    const job = runningJobs.get(jobId)

    if (!job) {
      // Job not found or already completed
      return {
        status: JobStatus.COMPLETED,
        progress: 100,
        message: 'Job completed',
      }
    }

    const elapsed = Date.now() - job.startTime
    const progress = Math.min(100, Math.floor((elapsed / job.duration) * 100))

    if (progress >= 100) {
      // Mark as completed
      runningJobs.delete(jobId)
      return {
        status: JobStatus.COMPLETED,
        progress: 100,
        message: 'Job completed successfully',
      }
    }

    return {
      status: JobStatus.RUNNING,
      progress,
      message: `Processing... ${progress}%`,
    }
  }
}

// Export singleton instance
export const mockModernizeAPIAdapter = new MockModernizeAPIAdapter()
